import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Sparkles, ArrowRight, Loader2, Copy, Twitter, Linkedin,
  Facebook, MessageCircle, TrendingUp, Zap, Edit, Share,
  Layers, DollarSign, ChevronRight, Mic
} from "lucide-react";
import { API, LOGO_URL } from "@/config";

const ICON_MAP = {
  twitter: Twitter,
  trending: TrendingUp,
  edit: Edit,
  dollar: DollarSign,
  sparkles: Sparkles,
  layers: Layers,
  share: Share,
  zap: Zap,
  mic: Mic,
};

export default function SeoTemplatePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allTemplates, setAllTemplates] = useState([]);

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const res = await axios.get(`${API}/seo/templates/${slug}`);
        setTemplate(res.data);
      } catch (e) {
        setTemplate(null);
      }
      setLoading(false);
    };
    const fetchAll = async () => {
      try {
        const res = await axios.get(`${API}/seo/templates`);
        setAllTemplates(res.data.filter(t => t.slug !== slug).slice(0, 3));
      } catch (e) {}
    };
    fetchTemplate();
    fetchAll();
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
    </div>
  );

  if (!template) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-4">Template Not Found</h1>
        <Link to="/"><Button className="rounded-full bg-violet-600 hover:bg-violet-700">Go Home</Button></Link>
      </div>
    </div>
  );

  const Icon = ICON_MAP[template.icon] || Sparkles;
  const shareUrl = `${window.location.origin}/t/${template.slug}`;
  const shareText = template.share_caption;

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(template.meta_title)}&summary=${encodeURIComponent(shareText)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
    whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + " " + shareUrl)}`,
  };

  const copyShareText = () => {
    navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
    toast.success("Share text copied!");
  };

  const CATEGORY_COLORS = {
    monetization: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    affiliate: "bg-teal-500/10 text-teal-400 border-teal-500/20",
    productivity: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    tools: "bg-purple-500/10 text-purple-400 border-purple-500/20",
    podcast: "bg-pink-500/10 text-pink-400 border-pink-500/20",
  };

  return (
    <>
      <Helmet>
        <title>{template.meta_title}</title>
        <meta name="description" content={template.meta_description} />
        <meta property="og:title" content={template.meta_title} />
        <meta property="og:description" content={template.meta_description} />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Creatara" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={template.meta_title} />
        <meta name="twitter:description" content={template.meta_description} />
        <meta name="keywords" content={`${template.keyword}, Creatara, content monetization, AI content creator`} />
        <link rel="canonical" href={shareUrl} />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Nav */}
        <nav className="fixed top-0 w-full z-50 glass">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3" data-testid="seo-logo">
              <img src={LOGO_URL} alt="Creatara - Free AI Content Monetization Tool" className="w-10 h-10 rounded-lg" />
              <span className="text-xl font-bold">Creatara</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/templates" className="text-muted-foreground hover:text-foreground text-sm hidden sm:block">All Templates</Link>
              <Button onClick={() => navigate("/register")} className="btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full px-6" data-testid="seo-get-started-btn">
                Get Started Free <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </nav>

        {/* Hero */}
        <section className="hero-glow pt-28 pb-16 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
              <Badge className={`mb-6 px-4 py-1 ${CATEGORY_COLORS[template.category] || "bg-violet-600/10 text-violet-500 border-violet-600/20"}`}>
                {template.category?.charAt(0).toUpperCase() + template.category?.slice(1)} Template
              </Badge>
              <div className="w-16 h-16 rounded-2xl bg-violet-600/20 flex items-center justify-center mx-auto mb-6">
                <Icon className="w-8 h-8 text-violet-500" />
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight gradient-text mb-4" data-testid="seo-template-title">
                {template.name}
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                {template.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={() => navigate(`/register?template=${template.slug}`)} size="lg" className="btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full px-8 h-14 text-base" data-testid="seo-cta-signup">
                  <Sparkles className="w-5 h-5 mr-2" /> Use This Template Free
                </Button>
                <Button onClick={() => navigate(`/login?template=${template.slug}`)} variant="outline" size="lg" className="rounded-full px-8 h-14 border-white/20 hover:bg-white/5" data-testid="seo-cta-login">
                  Sign In
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Template Details */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <Card className="glass border-violet-500/10">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold mb-6">What This Template Does</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-violet-600/20 flex items-center justify-center shrink-0 mt-0.5"><Sparkles className="w-4 h-4 text-violet-500" /></div>
                        <div><p className="font-medium">AI-Powered Generation</p><p className="text-sm text-muted-foreground">Enter your topic and get professional {template.content_type} content in seconds</p></div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-teal-500/20 flex items-center justify-center shrink-0 mt-0.5"><TrendingUp className="w-4 h-4 text-teal-500" /></div>
                        <div><p className="font-medium">Revenue-Optimized</p><p className="text-sm text-muted-foreground">Every piece of content is crafted to drive engagement and conversions</p></div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center shrink-0 mt-0.5"><Share className="w-4 h-4 text-blue-500" /></div>
                        <div><p className="font-medium">Multi-Platform Ready</p><p className="text-sm text-muted-foreground">Share to X/Twitter, LinkedIn, Facebook, and WhatsApp with one click</p></div>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                        <p className="text-xs text-muted-foreground mb-2">Template Settings</p>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between"><span className="text-muted-foreground">Content Type</span><Badge variant="secondary">{template.content_type}</Badge></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Tone</span><Badge variant="secondary">{template.tone}</Badge></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Length</span><Badge variant="secondary">{template.length}</Badge></div>
                        </div>
                      </div>
                      <div className="p-4 rounded-xl bg-gradient-to-r from-violet-600/10 to-violet-600/5 border border-violet-600/20">
                        <p className="text-sm font-medium text-violet-400 mb-1">Keyword Focus</p>
                        <p className="text-lg font-bold">{template.keyword}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* Share Section */}
        <section className="py-12 px-6 bg-card/30">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
              <h2 className="text-2xl font-bold text-center mb-2">Share This Template</h2>
              <p className="text-muted-foreground text-center mb-8">Spread the word and help others discover this tool</p>

              <Card className="glass">
                <CardContent className="p-6">
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10 mb-4">
                    <p className="text-sm text-muted-foreground mb-1">Pre-written share text:</p>
                    <p className="text-sm leading-relaxed">{shareText} <span className="text-violet-400">{shareUrl}</span></p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button onClick={copyShareText} variant="outline" className="rounded-full" data-testid="seo-copy-share">
                      <Copy className="w-4 h-4 mr-2" /> Copy Text
                    </Button>
                    <Button onClick={() => window.open(shareLinks.twitter, '_blank')} className="rounded-full bg-black hover:bg-gray-900 text-white" data-testid="seo-share-twitter">
                      <Twitter className="w-4 h-4 mr-2" /> Post on X
                    </Button>
                    <Button onClick={() => window.open(shareLinks.linkedin, '_blank')} className="rounded-full bg-blue-700 hover:bg-blue-800 text-white" data-testid="seo-share-linkedin">
                      <Linkedin className="w-4 h-4 mr-2" /> LinkedIn
                    </Button>
                    <Button onClick={() => window.open(shareLinks.facebook, '_blank')} className="rounded-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="seo-share-facebook">
                      <Facebook className="w-4 h-4 mr-2" /> Facebook
                    </Button>
                    <Button onClick={() => window.open(shareLinks.whatsapp, '_blank')} className="rounded-full bg-green-600 hover:bg-green-700 text-white" data-testid="seo-share-whatsapp">
                      <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* Other Templates */}
        {allTemplates.length > 0 && (
          <section className="py-16 px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold mb-8 text-center">More Revenue Templates</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {allTemplates.map((t) => {
                  const TIcon = ICON_MAP[t.icon] || Sparkles;
                  return (
                    <Link key={t.slug} to={`/t/${t.slug}`}>
                      <Card className="glass card-hover h-full">
                        <CardContent className="p-5">
                          <TIcon className="w-8 h-8 text-violet-500 mb-3" />
                          <h3 className="font-semibold mb-1">{t.name}</h3>
                          <p className="text-sm text-muted-foreground line-clamp-2">{t.description}</p>
                          <div className="flex items-center gap-1 mt-3 text-violet-500 text-sm font-medium">
                            Use template <ChevronRight className="w-4 h-4" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
              <div className="text-center mt-8">
                <Link to="/templates">
                  <Button variant="outline" className="rounded-full">View All Templates <ArrowRight className="w-4 h-4 ml-2" /></Button>
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* Final CTA */}
        <section className="py-20 px-6 bg-card/30">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to Monetize Your Content?</h2>
            <p className="text-lg text-muted-foreground mb-8">Join creators using Creatara to generate revenue-driving content in seconds.</p>
            <Button onClick={() => navigate("/register")} size="lg" className="btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full px-8 py-6 text-lg">
              Start Free — No Credit Card <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-border py-8 px-6">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3"><img src={LOGO_URL} alt="Creatara - Revenue Template for Content Creators" className="w-8 h-8 rounded-lg" /><span className="font-semibold">Creatara</span></div>
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <Link to="/templates" className="text-muted-foreground hover:text-foreground">All Templates</Link>
              <Link to="/privacy" className="text-muted-foreground hover:text-foreground">Privacy</Link>
              <Link to="/terms" className="text-muted-foreground hover:text-foreground">Terms</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
